import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { HeroSearch } from '@/components/HeroSearch';
import { PropertyCard } from '@/components/PropertyCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RevealOnScroll, StaggerReveal, ScaleReveal } from '@/components/animations/RevealOnScroll';
import SEOHead, { generateSchema } from '@/components/SEOHead';
import { CookieConsentBanner } from '@/components/CookieConsentBanner';
import { propertiesAPI } from '@/lib/api';
import { storage } from '@/lib/storage';
import { analytics } from '@/lib/analytics';
import { shouldFetch, markFetched } from '@/lib/fetchCache';
import { usePageTitle } from '@/hooks/usePageTitle';
import { ShieldCheck, DollarSign, Star, Waves, Search, Home as HomeIcon, ArrowRight, Sparkles, Settings, Crown } from 'lucide-react';
import { toast } from 'sonner';

const Home = () => {
  const navigate = useNavigate();
  const user = storage.getUser();
  const { t, i18n } = useTranslation();
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [diamondProperties, setDiamondProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const abortControllerRef = useRef(null);
  
  // Set page title
  usePageTitle('home');

  useEffect(() => {
    // Track page view
    analytics.pageView('Home');
    
    // Check module-level cache to prevent duplicate fetches (survives StrictMode remount)
    const cacheKey = 'home-properties';
    if (!shouldFetch(cacheKey)) {
      setLoading(false);
      return;
    }
    // Mark immediately to prevent StrictMode double-invoke
    markFetched(cacheKey);
    
    // Create abort controller for cleanup
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    const fetchData = async () => {
      try {
        const response = await propertiesAPI.getAll({ signal: abortController.signal });
        
        if (abortController.signal.aborted) return;
        
        const properties = response.data?.properties || response.data || [];
        
        const diamond = properties.filter(p => p.plan_type === 'diamond' && p.status === 'active');
        setDiamondProperties(diamond);
        
        const gold = properties.filter(p => p.plan_type === 'gold' && p.status === 'active');
        const featured = gold.length > 0 
          ? gold.slice(0, 3)
          : properties.filter(p => p.status === 'active' && p.plan_type !== 'diamond').slice(0, 3);
        
        setFeaturedProperties(featured);
      } catch (error) {
        if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
        console.error('Error fetching properties:', error);
        toast.error(t('errors.generic'));
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };
    
    fetchData();

    // Note: We don't abort in cleanup because the fetch should complete
    // even if the component unmounts briefly due to StrictMode
  }, []);

  const fetchFeaturedProperties = async () => {
    // This function is now only used for manual refresh if needed
    try {
      const response = await propertiesAPI.getAll();
      const properties = response.data?.properties || response.data || [];
      
      const diamond = properties.filter(p => p.plan_type === 'diamond' && p.status === 'active');
      setDiamondProperties(diamond);
      
      const gold = properties.filter(p => p.plan_type === 'gold' && p.status === 'active');
      const featured = gold.length > 0 
        ? gold.slice(0, 3)
        : properties.filter(p => p.status === 'active' && p.plan_type !== 'diamond').slice(0, 3);
      
      setFeaturedProperties(featured);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchProperties = () => {
    navigate('/search');
  };

  const handleListProperty = () => {
    if (!user) {
      toast.info(t('toast.loginRequired'));
      navigate('/login');
      return;
    }
    navigate('/minha-conta');
  };

  // Floating particles animation
  const FloatingParticle = ({ delay, duration, size, color, left, top }) => (
    <motion.div
      className={`absolute rounded-full ${color}`}
      style={{ width: size, height: size, left, top }}
      animate={{
        y: [0, -20, 0],
        opacity: [0.3, 0.8, 0.3],
        scale: [1, 1.2, 1],
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* SEO Head */}
      <SEOHead
        title={i18n.language === 'pt' || i18n.language === 'pt-BR' 
          ? 'Aluguel de Temporada em Orlando, Florida' 
          : 'Vacation Rentals in Orlando, Florida'}
        description={i18n.language === 'pt' || i18n.language === 'pt-BR'
          ? 'Encontre o imóvel perfeito para suas férias em Orlando. MagicStay Homes conecta você diretamente com proprietários para as melhores ofertas perto da Disney e Universal.'
          : 'Find your perfect vacation rental in Orlando. MagicStay Homes connects you directly with property owners for the best deals near Disney and Universal.'}
        keywords={['vacation rental', 'Orlando', 'Florida', 'Disney', 'Universal', 'holiday home', 'aluguel de temporada']}
        schema={generateSchema.organization()}
      />
      
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-cyan-50/30 to-blue-50/20" />
        
        {/* Floating Particles */}
        <FloatingParticle delay={0} duration={4} size={12} color="bg-[#FFC857]" left="5%" top="15%" />
        <FloatingParticle delay={1} duration={5} size={8} color="bg-[#00C2D1]" left="85%" top="20%" />
        <FloatingParticle delay={0.5} duration={6} size={10} color="bg-[#FFC857]" left="20%" top="70%" />
        <FloatingParticle delay={2} duration={4.5} size={6} color="bg-[#00C2D1]" left="75%" top="60%" />
        <FloatingParticle delay={1.5} duration={5.5} size={14} color="bg-[#FFC857]/50" left="50%" top="25%" />
        <FloatingParticle delay={0.8} duration={4} size={8} color="bg-[#00C2D1]/50" left="30%" top="45%" />
        
        {/* Noise overlay */}
        <div className="noise-overlay" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative py-12 md:py-20">
          <div className="max-w-4xl mx-auto text-center space-y-6 md:space-y-8">
            
            {/* Logo with hover animation */}
            <motion.div 
              className="flex justify-center mb-6"
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <motion.img 
                src="/logo.png" 
                alt="MagicStay Homes" 
                className="h-36 sm:h-44 md:h-52 lg:h-60 w-auto object-contain drop-shadow-xl"
                data-testid="hero-logo"
                whileHover={{ scale: 1.05, rotate: 2 }}
                transition={{ type: 'spring', stiffness: 300 }}
              />
            </motion.div>
            
            <RevealOnScroll>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#08172F] leading-tight tracking-tight">
                {t('home.welcome')}
              </h1>
            </RevealOnScroll>
            
            <RevealOnScroll delay={0.1}>
              <p className="text-base md:text-lg text-[#4B5563] max-w-2xl mx-auto leading-relaxed">
                {t('home.subtitle')}
              </p>
            </RevealOnScroll>

            {/* Choice Cards */}
            <StaggerReveal staggerDelay={0.15} className="grid md:grid-cols-2 gap-6 mt-10">
              {/* Search Card */}
              <motion.div whileHover={{ y: -8 }} transition={{ type: 'spring', stiffness: 300 }}>
                <Card 
                  className="glass-card rounded-2xl cursor-pointer group border-0 overflow-hidden h-full"
                  onClick={handleSearchProperties}
                  data-testid="hero-search-card"
                >
                  <CardContent className="p-8 text-center relative">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#00C2D1]/10 to-transparent rounded-bl-full" />
                    <motion.div 
                      className="w-16 h-16 bg-gradient-to-br from-[#00C2D1] to-[#00A8B5] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-[0_8px_20px_rgba(0,194,209,0.35)]"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ type: 'spring', stiffness: 400 }}
                    >
                      <Search className="w-8 h-8 text-white" />
                    </motion.div>
                    <h3 className="text-xl md:text-2xl font-semibold mb-3 text-[#08172F]">{t('home.searchTitle')}</h3>
                    <p className="text-[#6B7280] mb-6 leading-relaxed text-sm md:text-base">
                      {t('home.searchDescription')}
                    </p>
                    <Button className="bg-[#08172F] text-white hover:bg-[#0a2245] w-full rounded-xl shadow-lg h-12 group">
                      {t('home.startSearching')}
                      <motion.span className="ml-2" whileHover={{ x: 5 }}>
                        <ArrowRight className="w-4 h-4" />
                      </motion.span>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* List Card */}
              <motion.div whileHover={{ y: -8 }} transition={{ type: 'spring', stiffness: 300 }}>
                <Card 
                  className="glass-card rounded-2xl cursor-pointer group border-0 overflow-hidden h-full"
                  onClick={handleListProperty}
                  data-testid="hero-list-card"
                >
                  <CardContent className="p-8 text-center relative">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#FFC857]/15 to-transparent rounded-bl-full" />
                    <motion.div 
                      className="w-16 h-16 bg-gradient-to-br from-[#FFE9A3] to-[#FFC857] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-[0_8px_20px_rgba(255,200,87,0.45)]"
                      whileHover={{ scale: 1.1, rotate: -5 }}
                      transition={{ type: 'spring', stiffness: 400 }}
                    >
                      <HomeIcon className="w-8 h-8 text-[#0B1220]" />
                    </motion.div>
                    <h3 className="text-xl md:text-2xl font-semibold mb-3 text-[#08172F]">{t('home.listTitle')}</h3>
                    <p className="text-[#6B7280] mb-6 leading-relaxed text-sm md:text-base">
                      {t('home.listDescription')}
                    </p>
                    <Button className="bg-[#FFC857] text-[#0B1220] hover:bg-[#F5B942] w-full rounded-xl shadow-lg h-12 font-semibold group">
                      {t('home.startListing')}
                      <motion.span className="ml-2" whileHover={{ x: 5 }}>
                        <ArrowRight className="w-4 h-4" />
                      </motion.span>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </StaggerReveal>
          </div>

          {/* Search Bar */}
          <RevealOnScroll delay={0.3}>
            <div className="mt-10 max-w-6xl xl:max-w-7xl mx-auto">
              <HeroSearch />
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-20 bg-white" data-testid="features-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <RevealOnScroll>
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-2xl font-bold text-[#08172F] mb-3">{t('home.whyChoose')}</h2>
            </div>
          </RevealOnScroll>

          <StaggerReveal staggerDelay={0.1} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {[
              { icon: ShieldCheck, title: t('home.verifiedProperties'), desc: t('home.verifiedPropertiesDesc'), color: 'cyan' },
              { icon: DollarSign, title: t('home.noCommissions'), desc: t('home.noCommissionsDesc'), color: 'cyan' },
              { icon: Star, title: t('home.directContact'), desc: t('home.directContactDesc'), color: 'gold' },
              { icon: Waves, title: t('home.bestPrices'), desc: t('home.bestPricesDesc'), color: 'cyan' },
            ].map((feature, index) => (
              <motion.div key={index} whileHover={{ y: -5 }} transition={{ type: 'spring', stiffness: 300 }}>
                <Card className="text-center glass-card rounded-xl border-0 h-full">
                  <CardHeader>
                    <motion.div 
                      className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                        feature.color === 'gold' 
                          ? 'bg-gradient-to-br from-[#FFC857]/20 to-[#E6B64D]/20' 
                          : 'bg-gradient-to-br from-[#00C2D1]/20 to-[#00A8B5]/20'
                      }`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <feature.icon className={`w-7 h-7 ${feature.color === 'gold' ? 'text-[#FFC857]' : 'text-[#00C2D1]'}`} />
                    </motion.div>
                    <CardTitle className="text-base md:text-lg text-[#08172F]">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm text-[#6B7280] leading-relaxed">
                      {feature.desc}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </StaggerReveal>
        </div>
      </section>

      {/* CTA Banner for Logged Users - Become a Host */}
      {user && user.role !== 'host' && (
        <section className="py-12 bg-gradient-to-r from-[#00C2D1] via-[#00A8B5] to-[#008B94] relative overflow-hidden" data-testid="become-host-cta">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <RevealOnScroll>
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                      {i18n.language?.startsWith('pt') ? '🏠 Tem um imóvel para alugar?' : '🏠 Have a property to rent?'}
                    </h3>
                    <p className="text-white/90 text-base md:text-lg max-w-xl">
                      {i18n.language?.startsWith('pt') 
                        ? 'Anuncie gratuitamente e alcance milhares de hóspedes em busca do lugar perfeito!'
                        : 'List for free and reach thousands of guests looking for the perfect place!'}
                    </p>
                  </motion.div>
                </div>
                
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button 
                    onClick={() => navigate('/anunciar')}
                    className="bg-[#FFC857] text-[#0B1220] hover:bg-[#FFD97A] px-8 py-6 text-lg font-bold rounded-xl shadow-xl hover:shadow-2xl transition-all flex items-center gap-3"
                    data-testid="become-host-cta-btn"
                  >
                    <HomeIcon className="w-6 h-6" />
                    {i18n.language?.startsWith('pt') ? 'Anuncie seu Imóvel' : 'List Your Property'}
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </motion.div>
              </div>
            </RevealOnScroll>
          </div>
        </section>
      )}

      {/* Diamond Properties Section */}
      {diamondProperties.length > 0 && (
        <section className="py-16 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden" data-testid="diamond-properties-section">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div 
              className="absolute w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl"
              animate={{ x: [0, 50, 0], y: [0, 30, 0] }}
              transition={{ duration: 10, repeat: Infinity }}
              style={{ top: '10%', left: '10%' }}
            />
            <motion.div 
              className="absolute w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"
              animate={{ x: [0, -30, 0], y: [0, 50, 0] }}
              transition={{ duration: 12, repeat: Infinity }}
              style={{ bottom: '10%', right: '10%' }}
            />
          </div>
          
          <div className="container mx-auto px-4 relative">
            <RevealOnScroll>
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                    <motion.div
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                    >
                      <Sparkles className="w-8 h-8 text-yellow-400" />
                    </motion.div>
                    {t('home.diamondProperties')}
                  </h2>
                  <p className="text-gray-300 mt-1">{t('home.premiumPropertiesDesc')}</p>
                </div>
              </div>
            </RevealOnScroll>

            {/* Diamond Showcase */}
            <motion.div 
              className="bg-gradient-to-br from-amber-500/20 to-purple-600/20 rounded-3xl p-8 border-2 border-amber-400/30 backdrop-blur-xl shadow-2xl diamond-sparkle"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex flex-col md:flex-row items-center gap-6 mb-6">
                <div className="flex items-center gap-4">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <Sparkles className="w-16 h-16 text-amber-400" />
                  </motion.div>
                  <div>
                    <h3 className="text-3xl font-bold text-white">{t('home.exclusiveDiamond')}</h3>
                    <p className="text-amber-200 text-lg">{diamondProperties.length} {diamondProperties.length === 1 ? t('home.luxuryProperty') : t('home.luxuryProperties')}</p>
                  </div>
                </div>
                <div className="flex-1" />
                <motion.div 
                  className="bg-amber-400/20 px-6 py-3 rounded-full border-2 border-amber-400 glow-gold"
                  whileHover={{ scale: 1.05 }}
                >
                  <p className="text-white font-bold text-lg">⭐ {t('home.premiumSelection')}</p>
                </motion.div>
              </div>
              
              {diamondProperties.length > 0 && (
                <div className="mb-6">
                  <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
                    <PropertyCard property={diamondProperties[0]} />
                  </div>
                </div>
              )}
              
              {diamondProperties.length > 1 && (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-px flex-1 bg-amber-400/30" />
                    <p className="text-amber-200 text-sm font-semibold">{t('common.viewDetails')} ({diamondProperties.length - 1})</p>
                    <div className="h-px flex-1 bg-amber-400/30" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                    {diamondProperties.slice(1).map((property) => (
                      <motion.div 
                        key={property.id}
                        onClick={() => navigate(`/property/${property.id}`)}
                        className="bg-white/10 backdrop-blur-md rounded-xl overflow-hidden cursor-pointer border-2 border-white/20"
                        whileHover={{ scale: 1.03, borderColor: 'rgba(251, 191, 36, 0.5)' }}
                        transition={{ type: 'spring', stiffness: 300 }}
                      >
                        <div className="relative">
                          <img 
                            src={property.images?.[0]?.image_url || '/placeholder.jpg'} 
                            alt={property.title}
                            loading="lazy"
                            className="w-full h-48 object-cover"
                          />
                          <div className="absolute top-2 right-2 bg-amber-400 text-slate-900 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            DIAMOND
                          </div>
                        </div>
                        <div className="p-4">
                          <h4 className="text-white font-bold text-base mb-2 line-clamp-2">{property.title}</h4>
                          <p className="text-gray-300 text-sm mb-2">{property.city}, {property.state}</p>
                          <p className="text-amber-400 font-bold text-lg">${(property.price_per_night || property.price || property.nightly_price_suggestion || 0)}<span className="text-gray-400 text-xs ml-1">/{t('common.night')}</span></p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* Featured Properties */}
      <section className="py-16 bg-gray-50" data-testid="featured-properties-section">
        <div className="container mx-auto px-4">
          <RevealOnScroll>
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-3xl font-bold text-primary">{t('home.featuredProperties')}</h2>
              </div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant="outline" onClick={() => navigate('/search')} className="border-gray-300">
                  {t('home.viewAllProperties')}
                </Button>
              </motion.div>
            </div>
          </RevealOnScroll>

          {loading ? (
            <div className="text-center py-12">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="w-8 h-8 border-2 border-secondary border-t-transparent rounded-full mx-auto mb-4"
              />
              <p className="text-muted-foreground">{t('common.loading')}</p>
            </div>
          ) : featuredProperties.length > 0 ? (
            <StaggerReveal staggerDelay={0.1} className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredProperties.map((property, index) => (
                <PropertyCard key={property.id} property={property} index={index} />
              ))}
            </StaggerReveal>
          ) : (
            <div className="text-center py-12">
              <HomeIcon className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t('search.noResults')}</h3>
              <p className="text-muted-foreground mb-4">{t('search.noResultsDesc')}</p>
              <Button onClick={() => navigate('/search')}>
                {t('home.viewAllProperties')}
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 animated-gradient" />
        <div className="container mx-auto px-4 text-center relative">
          <RevealOnScroll>
            <h2 className="text-3xl font-bold mb-4 text-white">{t('home.findIdealProperty')}</h2>
            <p className="text-xl mb-8 text-white/90">{t('home.subtitle')}</p>
            <div className="flex gap-4 justify-center flex-wrap">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  size="lg" 
                  className="bg-white text-primary hover:bg-white/90"
                  onClick={handleSearchProperties}
                >
                  {t('home.searchTitle')}
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/10"
                  onClick={handleListProperty}
                >
                  {t('home.listTitle')}
                </Button>
              </motion.div>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* Admin Dashboard Shortcut - Only shows for admin users */}
      {user && user.role === 'admin' && (
        <section className="py-8 bg-gradient-to-r from-[#08172F] to-[#0B2246]" data-testid="admin-shortcut-section">
          <div className="container mx-auto px-4">
            <motion.div 
              className="flex flex-col md:flex-row items-center justify-between gap-4 p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-4">
                <div className="bg-[#FFC857]/20 p-3 rounded-xl">
                  <Crown className="h-8 w-8 text-[#FFC857]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Painel Administrativo</h3>
                  <p className="text-gray-300 text-sm">Gerencie usuários, propriedades e configurações do sistema</p>
                </div>
              </div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  size="lg"
                  className="bg-[#FFC857] text-[#08172F] hover:bg-[#E6B64D] font-bold gap-2"
                  onClick={() => navigate('/admin')}
                  data-testid="admin-dashboard-shortcut"
                >
                  <Settings className="w-5 h-5" />
                  Acessar Dashboard Admin
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>
      )}

      <Footer />
      
      {/* Cookie Consent Banner - Only shown on Home page */}
      <CookieConsentBanner />
    </div>
  );
};

export default Home;
